import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel } from 'swiper/modules';
import { useGetUpcomingGames, useTeamColors } from 'hooks';
import { PrevArrowIcon, NextArrowIcon } from 'assets/icons';
import getIndex from 'helpers/getIndex';
import types from 'redux/types';

import GameDate from 'components/GameDate';
import {
  Loading,
  GameInfoButton,
  ErrorMessage,
  Teams,
  TvChannel,
  GameResult,
} from 'components';

import 'swiper/swiper-bundle.css';
import { StyledTitle } from 'theme/titles.js';
import {
  StyledSection,
  LeftSideBar,
  RightSideBar,
  SlideContent,
  ArrowWrapper,
} from './style';

export const DashboardUpcomingGames = ({ setEventLeague } = {}) => {
  const { mainColor } = useTeamColors();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const leagueId = searchParams.get('leagueId');
  const league = searchParams.get('league');

  const {
    status: gamesStatus,
    data: games,
    isLoading: gamesIsLoading,
  } = useGetUpcomingGames(leagueId);

  // Sync React Query data to Redux for other components
  useEffect(() => {
    if (games?.data?.length) {
      const gameIndex = getIndex(games.data, true);
      dispatch({ type: types.GET_GAMES_SUCCESS, payload: games.data });
      dispatch({ type: types.GET_CURRENT_GAME, payload: gameIndex });
    } else if (gamesStatus === 'error') {
      dispatch({
        type: types.GET_GAMES_ERROR,
        payload:
          'Something went wrong. It seems there is no info about this team schedule. Please try again later',
      });
    }
  }, [games, gamesStatus, dispatch]);

  //** FIND event_id FUNCTION TO SET IN URL PARAM **//
  const slideChanged = useCallback(
    (index) => {
      console.log('[DashboardUpcomingGames] slideChanged called:', {
        index,
        totalGames: games?.data?.length,
        hasData: !!games?.data,
      });

      if (!games?.data?.length) {
        console.log('[DashboardUpcomingGames] No games data, skipping');
        return;
      }

      const game = games.data[index];
      console.log('[DashboardUpcomingGames] Game at index:', {
        index,
        hasGame: !!game,
        eventLeagueName: game?.event_league_name,
        eventId: game?.event_id,
      });

      const eventLeagueName = game?.event_league_name;
      if (eventLeagueName && setEventLeague) {
        console.log(
          '[DashboardUpcomingGames] Calling setEventLeague:',
          eventLeagueName
        );
        setEventLeague(eventLeagueName);
      } else if (!eventLeagueName) {
        console.warn(
          '[DashboardUpcomingGames] No event_league_name for game at index',
          index
        );
      }
    },
    [games, setEventLeague]
  );

  return (
    <>
      {gamesIsLoading ? <Loading /> : null}

      {!gamesIsLoading && gamesStatus === 'error' ? <ErrorMessage /> : null}

      {!gamesIsLoading && !games?.data?.length && league ? (
        <StyledTitle>{`There are no upcoming events for ${league}`}</StyledTitle>
      ) : null}

      {!gamesIsLoading && games?.data?.length ? (
        <>
          <LeftSideBar>
            <ArrowWrapper className="swiper-button-prev-unique">
              <PrevArrowIcon fill={mainColor} />
            </ArrowWrapper>
          </LeftSideBar>
          <StyledSection>
            <Swiper
              grabCursor={true}
              id="main-slider"
              initialSlide={0}
              modules={[Navigation, Mousewheel]}
              mousewheel={true}
              navigation={{
                prevEl: '.swiper-button-prev-unique',
                nextEl: '.swiper-button-next-unique',
              }}
              onInit={(swiper) => {
                console.log('[DashboardUpcomingGames] Swiper initialized:', {
                  slides: swiper.slides?.length,
                  activeIndex: swiper.activeIndex,
                });
              }}
              onSlideChange={(swiper) => {
                try {
                  console.log(
                    '[DashboardUpcomingGames] onSlideChange fired, activeIndex:',
                    swiper.activeIndex
                  );
                  slideChanged(swiper.activeIndex);
                } catch (error) {
                  console.error(
                    '[DashboardUpcomingGames] Error in onSlideChange:',
                    error
                  );
                }
              }}
              slidesPerView={1}
              tag="section"
              wrapperTag="ul"
            >
              {games?.data.map((game, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <SwiperSlide key={`${game.event_id}-${index}`} tag="li">
                  <SlideContent>
                    <Teams
                      eventType={game?.event_type}
                      location={game.event_location}
                      opponent={game.team_b_event_text}
                      team={game.team_a_event_text}
                    />
                    <GameInfoButton
                      city={game.location_city}
                      date={game.event_start}
                      eventLeagueName={game.event_league_name || league}
                      eventType={game?.event_type}
                      locationId={game.event_location_id}
                      opponent={game.event_text}
                      region={game.stadium_country}
                      stadium={game.stadium_name}
                      stadiumLocation={{
                        lat: game?.stadium_lat,
                        lng: game?.stadium_lng,
                      }}
                      team={game.team_a_event_text}
                      timeZone={game.stadium_time_zone}
                      zipCode={game.stadium_zip_code}
                      channel={game.event_tv_channel}
                      eventState={game.event_state}
                      headline={game.headline}
                    />
                    <GameDate
                      eventState={game.event_state}
                      eventTime={game.event_start}
                    />
                    <TvChannel channel={game.event_tv_channel} />
                    <GameResult
                      date={game.event_start}
                      eventNotesHeadline={game.event_notes_headline}
                      result={game.event_outcome}
                    />
                  </SlideContent>
                </SwiperSlide>
              ))}
            </Swiper>
          </StyledSection>
          <RightSideBar>
            <ArrowWrapper className="swiper-button-next-unique">
              <NextArrowIcon fill={mainColor} />
            </ArrowWrapper>
          </RightSideBar>
        </>
      ) : null}
    </>
  );
};
